/**
 * Shift pay rates (BON-245) — /api/v1/shift-pay-rates.
 *
 * Rates are an effective-dated series: saving appends a row taking effect on a
 * date, rather than overwriting a current value. That's what keeps a change
 * from repricing work already shown to someone.
 *
 * Shapes mirror ShiftRateEntry in
 * backend/src/services/shift-pay-rates.service.ts — keep in sync.
 */
import { apiFetch } from '../lib/apiFetch';
import { CREW_POSITION_LABELS } from '../lib/crewPositions';

/** The four rates. Not CrewPosition: `teacher_on_assistant_shift` is a
 *  combination, resolved against qualifications when pay is displayed. */
export const SHIFT_RATE_KEYS = [
  'teacher',
  'assistant',
  'teacher_in_training',
  'teacher_on_assistant_shift',
] as const;
export type ShiftRateKey = (typeof SHIFT_RATE_KEYS)[number];

/** The three crew positions reuse the canonical labels (BON-420); only the
 *  combination rate is this module's own, since it isn't a CrewPosition. */
export const SHIFT_RATE_LABELS: Record<ShiftRateKey, string> = {
  ...CREW_POSITION_LABELS,
  teacher_on_assistant_shift: 'Trained teacher on an assistant shift',
};

export interface ShiftRateEntry {
  rateKey: ShiftRateKey;
  amountCents: number;
  /** `YYYY-MM-DD` — when this rate took effect. */
  effectiveFromIso: string;
  /** Null on the seeded rows: the business's pre-existing rates, not an admin's. */
  setByUserId: string | null;
}

export async function fetchShiftPayRates(): Promise<ShiftRateEntry[]> {
  const res = await apiFetch('/api/v1/shift-pay-rates');
  if (!res.ok) throw new Error(`Failed to load pay rates (HTTP ${res.status})`);
  return ((await res.json()) as { rates: ShiftRateEntry[] }).rates;
}

export async function fetchShiftRateHistory(rateKey: ShiftRateKey): Promise<ShiftRateEntry[]> {
  const res = await apiFetch(`/api/v1/shift-pay-rates/${rateKey}/history`);
  if (!res.ok) throw new Error(`Failed to load rate history (HTTP ${res.status})`);
  return ((await res.json()) as { rates: ShiftRateEntry[] }).rates;
}

/** Set a rate. `effectiveFrom` defaults to today server-side; the server
 *  refuses a past date, since that would reprice work already shown. */
export async function setShiftPayRate(input: {
  rateKey: ShiftRateKey;
  amountCents: number;
  effectiveFrom?: string;
}): Promise<ShiftRateEntry> {
  const res = await apiFetch(`/api/v1/shift-pay-rates/${input.rateKey}`, {
    method: 'PUT',
    body: JSON.stringify({
      amountCents: input.amountCents,
      effectiveFrom: input.effectiveFrom,
    }),
  });
  let body: { rate?: ShiftRateEntry; message?: string } | null = null;
  try {
    body = (await res.json()) as { rate?: ShiftRateEntry; message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.rate) {
    throw new Error(body?.message ?? `Failed to save the rate (HTTP ${res.status})`);
  }
  return body.rate;
}
